import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Drawer, Empty, List, Space, Spin, Tooltip, Typography } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { Envelope, EnvelopeOpen } from "@phosphor-icons/react";
import dayjs from "dayjs";
import SynchronizationService from "../../services/synchronizationService";
import { useTheme } from "../../contexts/ThemeContext.jsx";
import { useNotifications } from "../../contexts/NotificationsContext.jsx";

const { Text } = Typography;

const formatWhen = (value) => {
  const d = dayjs(value);
  if (!d.isValid()) return "";
  return d.format("DD/MM/YYYY HH:mm");
};

const getAccentColor = ({ type, severity }) => {
  const t = String(type || "").toLowerCase();
  const s = String(severity || "").toLowerCase();

  if (t === "sync_error") return "#ff4d4f";
  if (t === "sync_success") return "#52c41a";
  if (t === "sync_missing_configuration") return "#faad14";

  if (t.includes("missing") || t.includes("configuration")) return "#faad14";
  if (t.includes("error")) return "#ff4d4f";
  if (t.includes("success")) return "#52c41a";
  if (t.includes("conclu") || t.includes("completed") || t.includes("done")) return "#52c41a";

  if (s === "error") return "#ff4d4f";
  if (s === "warning") return "#faad14";
  if (s === "info") return "#1677ff";
  return "#8c8c8c";
};

const friendlyType = (type) => {
  const t = String(type || "").trim();
  if (!t) return "";
  if (t.startsWith("sync_")) return "Sincronização";
  return t.replace(/_/g, " ");
};

const parseMetadata = (metadataJson) => {
  if (!metadataJson) return null;
  if (typeof metadataJson === "object") return metadataJson;
  const text = String(metadataJson || "").trim();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const getSyncId = (n) => {
  if (n?.syncId) return String(n.syncId);
  const meta = parseMetadata(n?.metadataJson);
  const candidate = meta?.syncId ?? meta?.SyncId ?? meta?.synchronizationId ?? meta?.SynchronizationId ?? null;
  return candidate ? String(candidate) : "";
};

const getSyncDisplayName = (sync) => {
  const v =
    sync?.name ??
    sync?.nome ??
    sync?.title ??
    sync?.nomeERP ??
    sync?.NomeERP ??
    sync?.erpName ??
    sync?.ERPName ??
    "";
  return String(v || "").trim();
};

const NotificationsDrawer = () => {
  const { theme, isDarkMode } = useTheme();
  const scrollRef = useRef(null);
  const {
    panelOpen,
    closePanel,
    list,
    listLoading,
    bulkLoading,
    listError,
    hasMore,
    loadMore,
    loadFirstPage,
    markAllRead,
    markRead,
    markUnread,
    navigateToSync,
    unread,
  } = useNotifications();

  const surface = theme?.cardBackground || (isDarkMode ? "#242526" : "#ffffff");
  const background = theme?.backgroundSecondary || (isDarkMode ? "#242526" : "#ffffff");
  const border = theme?.borderSecondary || theme?.border || (isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)");
  const textPrimary = theme?.textPrimary || (isDarkMode ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.88)");
  const textSecondary = theme?.textSecondary || (isDarkMode ? "rgba(255,255,255,0.70)" : "rgba(0,0,0,0.65)");
  const hoverBorder = isDarkMode ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.12)";
  const baseShadow = isDarkMode ? "rgba(0,0,0,0.40)" : "rgba(18, 18, 23, 0.05)";
  const hoverShadow = isDarkMode ? "rgba(0,0,0,0.58)" : "rgba(18, 18, 23, 0.09)";

  const [syncNameById, setSyncNameById] = useState({});
  const syncListFetchRef = useRef({ lastFetchAt: 0, inFlight: null });

  useEffect(() => {
    if (!panelOpen) return;
    const now = Date.now();
    const throttleMs = 60000;
    if (syncListFetchRef.current.inFlight) return;
    if (now - syncListFetchRef.current.lastFetchAt < throttleMs) return;

    let cancelled = false;
    const p = Promise.resolve()
      .then(async () => {
        const result = await SynchronizationService.getSynchronizationsSimplified({
          page: 1,
          pageSize: 2000,
        });

        const rawSyncs = Array.isArray(result)
          ? result
          : Array.isArray(result?.data)
            ? result.data
            : Array.isArray(result?.response)
              ? result.response
              : [];

        const nextMap = {};
        for (const s of rawSyncs) {
          const id = String(s?.id || s?.Id || "").trim();
          if (!id) continue;
          const name = getSyncDisplayName(s);
          nextMap[id] = name || "";
        }

        if (cancelled) return;
        setSyncNameById((prev) => ({ ...(prev || {}), ...nextMap }));
        syncListFetchRef.current.lastFetchAt = Date.now();
      })
      .finally(() => {
        syncListFetchRef.current.inFlight = null;
      });

    syncListFetchRef.current.inFlight = p;

    return () => {
      cancelled = true;
    };
  }, [panelOpen]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) loadMore();
  };

  return (
    <Drawer
      open={panelOpen}
      onClose={closePanel}
      title={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15 }}>
            <Text strong style={{ color: textPrimary }}>Notificações</Text>
            <Text style={{ fontSize: 12, opacity: 0.7, color: textSecondary }}>
              {unread ? `${unread} não lida${unread === 1 ? "" : "s"}` : "Tudo em dia"}
            </Text>
          </div>
          <Button
            size="small"
            onClick={() => markAllRead()}
            disabled={!unread || listLoading || bulkLoading}
            loading={bulkLoading}
            aria-label="Marcar todas como lidas"
          >
            Marcar todas como lidas
          </Button>
        </div>
      }
      placement="right"
      width={420}
      destroyOnClose={false}
      styles={{
        header: { background, borderBottom: `1px solid ${border}` },
        body: { background, padding: 16 },
      }}
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {listError ? (
          <Alert
            type="error"
            showIcon
            message="Não foi possível carregar as notificações."
            action={
              <Button size="small" onClick={() => loadFirstPage()}>
                Tentar novamente
              </Button>
            }
          />
        ) : null}

        <div
          ref={scrollRef}
          onScroll={onScroll}
          style={{
            height: "100%",
            overflowY: "auto",
            paddingRight: 6,
          }}
        >
          {!listLoading && !listError && !list.length ? (
            <Empty description="Nenhuma notificação encontrada" />
          ) : (
            <List
              dataSource={list}
              renderItem={(n) => {
                const when = formatWhen(n?.createdAt);
                const title = n?.title || n?.type || "Notificação";
                const message = n?.message || "";
                const isRead = Boolean(n?.isRead);
                const type = n?.type || "";
                const severity = n?.severity || "";
                const accent = getAccentColor({ type, severity });
                const syncId = getSyncId(n);
                const willNavigate = Boolean(syncId);
                const kind = friendlyType(type);
                const syncName = syncId ? syncNameById?.[syncId] || "" : "";
                return (
                  <List.Item
                    style={{
                      cursor: "default",
                      borderRadius: 12,
                      padding: 0,
                      marginBottom: 8,
                      background: surface,
                      border: `1px solid ${border}`,
                      borderLeft: `4px solid ${accent}`,
                      boxShadow: `0 10px 22px ${baseShadow}`,
                      overflow: "hidden",
                      transition:
                        "box-shadow 160ms ease, transform 160ms ease, border-top-color 160ms ease, border-right-color 160ms ease, border-bottom-color 160ms ease",
                      width: "100%",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.borderTopColor = hoverBorder;
                      e.currentTarget.style.borderRightColor = hoverBorder;
                      e.currentTarget.style.borderBottomColor = hoverBorder;
                      e.currentTarget.style.boxShadow = `0 14px 30px ${hoverShadow}`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.borderTopColor = border;
                      e.currentTarget.style.borderRightColor = border;
                      e.currentTarget.style.borderBottomColor = border;
                      e.currentTarget.style.boxShadow = `0 10px 22px ${baseShadow}`;
                    }}
                  >
                    <div style={{ display: "flex", width: "100%", minWidth: 0 }}>
                      <div style={{ flex: 1, padding: 12, display: "flex", gap: 12, alignItems: "flex-start", minWidth: 0 }}>
                        <Tooltip title={isRead ? "Marcar como não lida" : "Marcar como lida"}>
                          <button
                            type="button"
                            aria-label={isRead ? "Marcar como não lida" : "Marcar como lida"}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (isRead) markUnread(n?.id);
                              else markRead(n?.id);
                            }}
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 10,
                              background: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.03)",
                              color: textSecondary,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.06)"}`,
                              cursor: "pointer",
                              padding: 0,
                            }}
                          >
                            {isRead ? <EnvelopeOpen size={18} /> : <Envelope size={18} weight="fill" />}
                          </button>
                        </Tooltip>

                        <div style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, minWidth: 0 }}>
                            <Text strong style={{ minWidth: 0, flex: 1 }} ellipsis={{ tooltip: title }}>
                              {title}
                            </Text>
                            
                          </div>

                          {message ? (
                            <div
                              style={{
                                display: "-webkit-box",
                                fontSize: 13,
                                opacity: 0.85,
                                color: textSecondary,
                                overflow: "hidden",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 2,
                                wordBreak: "break-word",
                              }}
                            >
                              {message}
                            </div>
                          ) : null}
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                              {!isRead ? <div style={{ width: 8, height: 8, borderRadius: 999, background: accent }} /> : null}
                              {when ? <small style={{ fontSize: 12, opacity: 0.7, color: textSecondary, whiteSpace: "nowrap" }}>{when}</small> : null}
                            </div>

                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, minWidth: 0 }}>
                            <Text style={{ fontSize: 12, opacity: 0.65, minWidth: 0, flex: 1 }} ellipsis={{ tooltip: syncName ? `${kind ? kind : "Notificação"} • ${syncName}` : kind ? kind : "Notificação" }}>
                              {syncName ? `${kind ? kind : "Notificação"} • ${syncName}` : kind ? kind : "Notificação"}
                            </Text>
                            {willNavigate ? (
                              <Tooltip title="Abrir sincronização">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<RightOutlined style={{ color: accent }} />}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!isRead) markRead(n?.id);
                                    navigateToSync(n);
                                  }}
                                  aria-label="Abrir sincronização"
                                />
                              </Tooltip>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />
          )}

          {listLoading ? (
            <div style={{ padding: 12, display: "flex", justifyContent: "center" }}>
              <Spin />
            </div>
          ) : null}

          {!listLoading && hasMore ? (
            <div style={{ padding: 12, display: "flex", justifyContent: "center" }}>
              <Button size="small" onClick={() => loadMore()} aria-label="Carregar mais notificações">
                Carregar mais
              </Button>
            </div>
          ) : null}
        </div>
      </Space>
    </Drawer>
  );
};

export default NotificationsDrawer;
